import { treaty } from "@elysiajs/eden";
import type { App } from "@app/api";

// Base URL for the API
// In development: proxied via Vite (/api -> localhost:3000)
// In production: proxied via nginx (/api -> api:3000)
// Eden Treaty treats its argument as a host, so a path-only value like the
// `/api` in .env.example would become `https://api/…`. Resolve anything
// relative against the page origin so both forms work.
const configuredApiUrl: string | undefined = import.meta.env.VITE_API_URL;
const API_URL =
  configuredApiUrl && !configuredApiUrl.startsWith("/")
    ? configuredApiUrl
    : `${window.location.origin}${configuredApiUrl ?? "/api"}`;

// Auth token storage keys
const AUTH_TOKEN_KEY = "cc_auth_token";
const AUTH_EXPIRES_KEY = "cc_auth_expires";

/**
 * Get the current auth token from localStorage
 * Returns null if token is expired or doesn't exist
 */
function getAuthToken(): string | null {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const expiresStr = localStorage.getItem(AUTH_EXPIRES_KEY);

  if (!token || !expiresStr) return null;

  const expiresAt = new Date(expiresStr);
  if (expiresAt <= new Date()) {
    // Token expired, clear it
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_EXPIRES_KEY);
    return null;
  }

  return token;
}

/**
 * Create Eden Treaty client with dynamic auth headers
 * Headers are added dynamically on each request
 */
export const api = treaty<App>(API_URL, {
  headers: (): Record<string, string> => {
    const token = getAuthToken();
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  },
});

// Export types for convenience
export type { App };
